import { throttle } from "lodash-es";
import React, { ReactElement, useCallback, useRef, useState } from "react";
import { GroupBase, OptionsOrGroups } from "react-select";

import { isDefined } from "@utils/helpers";
import { useWoosmapServices } from "@utils/hooks/useWoosmapServices.hook";

import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";

import { StoreSearchInputComponent } from "./storeSearchInput.component";
import {
    fetchAddressSearchItems,
    fetchStoreSearchItems,
} from "./storeSearchInput.helpers";
import "./storeSearchInput.scss";
import { SearchItem, SearchItemType } from "./storeSearchInput.types";

export type SetOptionsCallback = (
    options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>,
) => void;

type StoreSearchInputProps = {
    messageId: string;
    value?: SearchItem;
    itemsToRetrieve: Map<SearchItemType, number>;
    placeholderText: string;
    loadingText: string;
    noResultsText: string;
    buttonContent: ReactElement;
    "aria-label": string;
    clearValue: () => void;
    onUseLocation: (latitude: number, longitude: number) => void;
    onSelect: (value: SearchItem | null) => void;
};

const StoreSearchInput = ({
    messageId,
    value,
    itemsToRetrieve,
    placeholderText,
    loadingText,
    noResultsText,
    buttonContent,
    "aria-label": ariaLabel,
    clearValue,
    onUseLocation,
    onSelect,
}: StoreSearchInputProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { LocalitiesService, StoresService } = useWoosmapServices();

    const inputValueRef = useRef<string | undefined>(undefined);
    const setOptionsCallbackRef = useRef<SetOptionsCallback | undefined>(
        undefined,
    );

    const updateOptions = useCallback(
        throttle(async () => {
            const inputValue = inputValueRef.current;
            const setOptions = setOptionsCallbackRef.current;

            if (!isDefined(inputValue) || !isDefined(setOptions)) return;
            if (inputValue.length === 0) return setOptions([]);

            try {
                setIsLoading(true);

                const searchItemPromises = [];

                if (itemsToRetrieve.has(SearchItemType.Store)) {
                    searchItemPromises.push(
                        fetchStoreSearchItems(
                            StoresService,
                            inputValue,
                            itemsToRetrieve.get(SearchItemType.Store),
                        ),
                    );
                }

                if (itemsToRetrieve.has(SearchItemType.Locality)) {
                    searchItemPromises.push(
                        fetchAddressSearchItems(
                            LocalitiesService,
                            inputValue,
                            itemsToRetrieve.get(SearchItemType.Locality),
                        ),
                    );
                }

                const results = (await Promise.all(searchItemPromises)).flat();

                setOptions(results);
            } catch {
                setOptions([]);
            } finally {
                setIsLoading(false);
            }
        }, 500),
        [LocalitiesService, StoresService, setIsLoading],
    );

    const loadOptions = (
        inputValue: string,
        callback: (
            options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>,
        ) => void,
    ) => {
        inputValueRef.current = inputValue;
        setOptionsCallbackRef.current = callback;

        updateOptions();
    };

    const handleOnLocationError = (message: string) =>
        broadcastMessage({
            id: messageId,
            text: message,
            status: "danger",
            dismissible: true,
        });

    return (
        <StoreSearchInputComponent
            isLoading={isLoading}
            value={value}
            loadOptions={loadOptions}
            placeholderText={placeholderText}
            loadingText={loadingText}
            noResultsText={noResultsText}
            buttonContent={buttonContent}
            aria-label={ariaLabel}
            onUseLocation={onUseLocation}
            onLocationError={handleOnLocationError}
            onSelect={onSelect}
            onFocus={clearValue}
        />
    );
};

export { StoreSearchInput };
